import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName } from "helpers/reports";
import { formatNumber } from "helpers/system";
import React, { useMemo } from "react";
import { numbersList } from "sources/books/trucks";
import CommonReport from "./commonReport";

const AveragePriceFooter = ({ rows, priceField, volumeField }) => {
    const { totalPrice, totalLoadVolume } = useMemo(
        () =>
            rows.reduce(
                (acc, item) => ({
                    totalLoadVolume: acc.totalLoadVolume + (item.isGrouped ? 0 : item.values[volumeField]),
                    totalPrice: acc.totalPrice + (item.isGrouped ? 0 : item.values[priceField]),
                }),
                {
                    totalLoadVolume: 0,
                    totalPrice: 0,
                },
            ),
        [priceField, rows, volumeField],
    );

    return <strong>{formatNumber(totalPrice / totalLoadVolume)}</strong>;
};

const TotalFooter = ({ rows, column }) => {
    const total = useMemo(
        () => rows.reduce((acc, item) => acc + (item.isGrouped ? 0 : item.values[columnName(column)]), 0),
        [column, rows],
    );

    return <strong>{formatNumber(total)}</strong>;
};

export default class PlanFactRefuelingsReport extends CommonReport {
    extractColumns = () => {
        const { columns } = this.props.data || {};
        return columns
            .filter((item) => item.propertyName !== "CarNumber")
            .map((column) => {
                return {
                    ...this.cellProps(column),
                    ...(column.propertyName === "CarId"
                        ? {
                              Cell: (props) => {
                                  const { subRows } = props.row;
                                  const carNumber = subRows[0].original?.carNumber || "Без машины";
                                  const rowCount = /\(\d+\)$/.test(carNumber) ? "" : ` (${subRows.length})`;
                                  return (
                                      <span data-test-id="car-number">
                                          {carNumber}
                                          {rowCount}
                                      </span>
                                  );
                              },
                              Filter: reportFilterWrapper((props) => (
                                  <SelectColumnFilter
                                      {...props}
                                      selectProps={{
                                          ...defaultMultipleSelectProps,
                                          url: numbersList,
                                          inputProps: { name: "carIds" },
                                          propName: "number",
                                          components: { ValueContainer },
                                      }}
                                  />
                              )),
                              filter: (rows, _id, filterValue) => {
                                  if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                                      return rows;
                                  }
                                  return rows.filter((row) => filterValue.value.includes(row.values.carId));
                              },
                          }
                        : {}),
                    aggregate: ["BidId", "CarNumber", "StartDate", "EndDate"].includes(column.propertyName)
                        ? undefined
                        : "sum",
                    Aggregated: (props) => {
                        if (column.propertyName === "PlanAveragePrice") {
                            const { planTotalPrice, planTotalLoadVolume } = props.groupedRows.find(
                                (item) => item.id === props.row.id,
                            ).values;
                            return (
                                <div className="long-text">
                                    {formatNumber(planTotalPrice / planTotalLoadVolume, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            );
                        }
                        if (column.propertyName === "FactAveragePrice") {
                            const { factTotalPrice, factTotalLoadVolume } = props.groupedRows.find(
                                (item) => item.id === props.row.id,
                            ).values;
                            return (
                                <div className="long-text">
                                    {formatNumber(factTotalPrice / factTotalLoadVolume, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            );
                        }
                        return <div className="long-text">{formatNumber(props.value)}</div>;
                    },
                    Footer: (info) => {
                        if (["BidId", "CarId", "StartDate", "EndDate"].includes(column.propertyName)) {
                            return undefined;
                        }
                        if (column.propertyName === "FactAveragePrice") {
                            return (
                                <AveragePriceFooter
                                    rows={info.flatRows}
                                    priceField="factTotalPrice"
                                    volumeField="factTotalLoadVolume"
                                />
                            );
                        } else if (column.propertyName === "PlanAveragePrice") {
                            return (
                                <AveragePriceFooter
                                    rows={info.flatRows}
                                    priceField="planTotalPrice"
                                    volumeField="planTotalLoadVolume"
                                />
                            );
                        } else {
                            return <TotalFooter rows={info.flatRows} column={column} />;
                        }
                    },
                };
            });
    };

    tableExtraProps = () => ({
        initialState: {
            groupBy: ["carId"],
        },
    });
}
